import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useCart } from "../cart/CartContext";

const ProductCard = ({ product, index }) => {
  const { add } = useCart();
  const navigate = useNavigate();

  const quickOrder = () => {
    const size =
      product.sizes?.find((s) => s.price === product.price) ||
      product.sizes?.find((s) => s.price);
    if (!size) {
      navigate(`/oils/${product.slug}`);
      return;
    }
    add({ slug: product.slug, name: product.name, image: product.cart_image || product.image, size: size.label, price: size.price });
    navigate("/cart");
  };

  return (
  <motion.article
    data-testid={`product-card-${product.slug}`}
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay: (index % 2) * 0.15, ease: [0.16, 1, 0.3, 1] }}
    className={`group ${index % 2 === 1 ? "md:translate-y-20" : ""}`}
  >
    <Link to={`/oils/${product.slug}`} data-testid={`product-link-${product.slug}`} className="block">
      <div className="relative overflow-hidden rounded-t-[10rem] rounded-b-3xl border border-ink/10">
        <img
          src={product.image}
          alt={`PHUDG ${product.name}`}
          loading="lazy"
          className="w-full aspect-[4/5] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute top-5 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-ink/70 backdrop-blur-sm px-4 py-1.5 text-[10px] tracking-[0.3em] uppercase text-gold">
            {product.unit || "Cold-Pressed"}
          </span>
        </div>
      </div>
    </Link>

    <div className="pt-8 px-1">
      <p className="text-[10px] tracking-[0.3em] uppercase text-amberbrand mb-2">
        {product.tagline}
      </p>
      <Link to={`/oils/${product.slug}`}>
        <h3 className="font-serif text-3xl md:text-4xl text-ink mb-3 hover:text-amberbrand transition-colors duration-300">
          {product.name}
        </h3>
      </Link>
      <p className="text-sm font-light text-ink/60 leading-relaxed mb-6 max-w-md">
        {product.description}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <p data-testid={`product-price-${product.slug}`} className="font-serif text-2xl text-ink">
          {product.price ? (
            <>
              ₹{product.price}
              <span className="text-sm text-ink/50 font-sans font-light"> / {product.unit}</span>
            </>
          ) : (
            <span className="text-lg italic text-ink/50">Price on enquiry</span>
          )}
        </p>
        <span className="rounded-full bg-olive/10 px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-olive">
          Free delivery
        </span>
        <motion.button
          data-testid={`buy-btn-${product.slug}`}
          onClick={quickOrder}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream hover:bg-gold hover:text-ink transition-colors duration-300"
        >
          <MessageCircle size={16} />
          Buy on WhatsApp
        </motion.button>
      </div>
    </div>
  </motion.article>
);
};

export const Products = ({ products }) => {
  const available = products.filter((p) => p.status === "available" && !p.combo);

  return (
    <section id="oils" data-testid="products-section" className="relative bg-cream py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-20 md:mb-28 md:flex md:items-end md:justify-between gap-10">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4"
            >
              01 — The Oils
            </motion.p>
            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "100%" }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="font-serif text-5xl md:text-7xl text-ink tracking-tighter"
              >
                From seed <span className="italic text-olive">to soul.</span>
              </motion.h2>
            </div>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-6 md:mt-0 max-w-sm text-sm font-light text-ink/60 leading-relaxed"
          >
            Four oils. One process. Pressed slowly in a wooden ghani, below
            45°C, in small batches — and every batch is lab-tested.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-x-10 gap-y-16 md:gap-y-8 md:pb-20">
          {available.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
